import { memo } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  Handle,
  MarkerType,
  Panel,
  Position,
  BackgroundVariant,
  type Node,
  type NodeProps,
  type NodeTypes,
  type Edge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { isRelease263Enabled } from '@/config/release263Features';

const COLORS = {
  root: '#0a104f',
  gda: '#1a237e',
  causalOrContributing: '#3771c8',
  biomarker: '#be0080',
  therapeuticTarget: '#d97706',
  nonCodingRna: '#0891b2',
  alteredExpression: '#7a4fa0',
  postTranslational: '#0b7285',
  genomicAlterations: '#1f6feb',
  epigenomicAlterations: '#16a34a',
  geneticVariation: '#7c3aed',
} as const;

type HandleDef = {
  id: string;
  type: 'source' | 'target';
  pos: 'left' | 'right' | 'top' | 'bottom';
};

interface OntologyNodeData extends Record<string, unknown> {
  title: string;
  headerColor: string;
  borderColor: string;
  handles: HandleDef[];
}

type OntologyEntityNode = Node<OntologyNodeData, 'ontologyEntity'>;

const POS_MAP: Record<string, Position> = {
  left: Position.Left,
  right: Position.Right,
  top: Position.Top,
  bottom: Position.Bottom,
};

const OntologyEntityNodeView = memo(function OntologyEntityNodeView({
  id,
  data,
}: NodeProps<OntologyEntityNode>) {
  const { title, headerColor, borderColor, handles } = data;
  return (
    <div
      style={{
        border: `2px solid ${borderColor}`,
        borderRadius: 10,
        background: '#fff',
        width: 190,
        boxShadow: '0 4px 18px rgba(0,0,0,0.10)',
        fontFamily: 'Arial, sans-serif',
        overflow: 'hidden',
      }}
    >
      {handles.map(h => (
        <Handle
          key={h.id}
          id={h.id}
          type={h.type}
          position={POS_MAP[h.pos]}
          style={{ background: borderColor, width: 9, height: 9, border: '2px solid #fff' }}
          aria-label={`${id}-${h.id}`}
        />
      ))}
      <div style={{ background: headerColor, padding: '10px 12px' }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 800,
            color: '#fff',
            lineHeight: 1.3,
            textAlign: 'center',
            whiteSpace: 'normal',
          }}
        >
          {title}
        </div>
      </div>
      <div style={{ height: 8 }} />
    </div>
  );
});

const NODE_TYPES: NodeTypes = {
  ontologyEntity: OntologyEntityNodeView as NodeTypes[string],
};

function h(nodeId: string, which: 'top' | 'bottom') {
  return `${nodeId}-${which}`;
}

function node(id: string, x: number, y: number, title: string, color: string): OntologyEntityNode {
  return {
    id,
    type: 'ontologyEntity',
    position: { x, y },
    data: {
      title,
      headerColor: color,
      borderColor: color,
      handles: [
        { id: h(id, 'top'), type: 'target', pos: 'top' },
        { id: h(id, 'bottom'), type: 'source', pos: 'bottom' },
      ],
    },
  };
}

function edge(id: string, source: string, target: string, color: string): Edge {
  return {
    id,
    source,
    target,
    sourceHandle: h(source, 'bottom'),
    targetHandle: h(target, 'top'),
    type: 'smoothstep',
    markerEnd: { type: MarkerType.ArrowClosed, color, width: 14, height: 14 },
    style: { stroke: color, strokeWidth: 2 },
  };
}

const C = COLORS;

const BASE_NODES: OntologyEntityNode[] = [
  // L0
  node('association', 1135, 0, 'Association', C.root),

  // L1
  node('gda', 1135, 140, 'Gene Disease Association', C.gda),

  // L2 — direct children of GDA (and Biomarker child)
  node('cell_marker', 0, 280, 'Cell marker', C.biomarker),
  node('biomarker', 200, 280, 'Biomarker', C.biomarker),
  node('therapeutic_target', 400, 280, 'Therapeutic Target', C.therapeuticTarget),
  node('non_coding_rna_regulation', 620, 280, 'Non-coding RNA Regulation', C.nonCodingRna),
  node('causal_or_contributing', 1135, 280, 'Causal or Contributing', C.causalOrContributing),

  // L3 — children of Causal or Contributing
  node('epigenomic_alterations', 80, 420, 'Epigenomic Alterations', C.epigenomicAlterations),
  node('genomic_alterations', 450, 420, 'Genomic Alterations', C.genomicAlterations),
  node('altered_expression', 1385, 420, 'Altered Expression', C.alteredExpression),
  node('post_translational', 1730, 420, 'Post-translational Modification', C.postTranslational),

  // L4 — children of Genomic Alterations
  node('chromosomal_rearrangement', 80, 560, 'Chromosomal Rearrangement', C.genomicAlterations),
  node('genetic_variation', 350, 560, 'Genetic Variation', C.genomicAlterations),
  node('fusion_gene', 620, 560, 'Fusion Gene', C.genomicAlterations),
  node('cnv', 890, 560, 'CNV', C.genomicAlterations),

  // L4 — child of Post-translational
  node('phosphorylation', 1730, 560, 'Phosphorylation', C.postTranslational),

  // L5 — children of Genetic Variation
  node('drug_response_variant', -50, 700, 'Drug Response Variant', C.geneticVariation),
  node('susceptibility_mutation', 170, 700, 'Susceptibility Mutation', C.geneticVariation),
  node('causal_mutation', 370, 700, 'Causal Mutation', C.geneticVariation),
  node('modifying_mutation', 580, 700, 'Modifying Mutation', C.geneticVariation),
  node('protective_variant', 800, 700, 'Protective Variant', C.geneticVariation),

  // L6 — child of Drug Response Variant
  node('pharmacogenomics', -50, 840, 'Pharmacogenomics', C.geneticVariation),

  // L6 — children of Causal Mutation
  node('somatic_causal_mutation', 280, 840, 'Somatic Causal Mutation', C.geneticVariation),
  node('germline_causal_mutation', 480, 840, 'Germline Causal Mutation', C.geneticVariation),

  // L6 — children of Modifying Mutation
  node('somatic_modifying_mutation', 680, 840, 'Somatic Modifying Mutation', C.geneticVariation),
  node('germline_modifying_mutation', 890, 840, 'Germline Modifying Mutation', C.geneticVariation),
];

const RELEASE_263_NODES: OntologyEntityNode[] = [
  // L3 — children of Causal or Contributing
  node('accumulation_and_aggregation', 1960, 420, 'Accumulation and Aggregation', C.causalOrContributing),
  node('activity', 2190, 420, 'Activity', C.causalOrContributing),

  // L4 — children of Altered Expression
  node('upregulation', 1270, 560, 'Upregulation', C.alteredExpression),
  node('downregulation', 1500, 560, 'Downregulation', C.alteredExpression),

  // L5 — child of Genetic Variation
  node('qtl', 1040, 700, 'QTL', C.geneticVariation),
];

const BASE_EDGES: Edge[] = [
  // L0 → L1
  edge('e-assoc-gda', 'association', 'gda', C.gda),

  // L1 → L2
  edge('e-gda-therapeutic', 'gda', 'therapeutic_target', C.therapeuticTarget),
  edge('e-gda-bio', 'gda', 'biomarker', C.biomarker),
  edge('e-gda-ncrna', 'gda', 'non_coding_rna_regulation', C.nonCodingRna),
  edge('e-gda-causal', 'gda', 'causal_or_contributing', C.causalOrContributing),

  // Biomarker → Cell marker
  edge('e-bio-cell', 'biomarker', 'cell_marker', C.biomarker),

  // Causal or Contributing → L3
  edge('e-causal-epigeno', 'causal_or_contributing', 'epigenomic_alterations', C.epigenomicAlterations),
  edge('e-causal-genomic', 'causal_or_contributing', 'genomic_alterations', C.genomicAlterations),
  edge('e-causal-altexpr', 'causal_or_contributing', 'altered_expression', C.alteredExpression),
  edge('e-causal-posttrans', 'causal_or_contributing', 'post_translational', C.postTranslational),

  // Genomic Alterations → Chromosomal Rearrangement + Genetic Variation + Fusion Gene + CNV
  edge('e-genomic-chr', 'genomic_alterations', 'chromosomal_rearrangement', C.genomicAlterations),
  edge('e-genomic-gv', 'genomic_alterations', 'genetic_variation', C.genomicAlterations),
  edge('e-genomic-fusion', 'genomic_alterations', 'fusion_gene', C.genomicAlterations),
  edge('e-genomic-cnv', 'genomic_alterations', 'cnv', C.genomicAlterations),

  // Post-translational → Phosphorylation
  edge('e-posttrans-phos', 'post_translational', 'phosphorylation', C.postTranslational),

  // Genetic Variation → L5
  edge('e-gv-drug', 'genetic_variation', 'drug_response_variant', C.geneticVariation),
  edge('e-gv-sus', 'genetic_variation', 'susceptibility_mutation', C.geneticVariation),
  edge('e-gv-causal', 'genetic_variation', 'causal_mutation', C.geneticVariation),
  edge('e-gv-modifying', 'genetic_variation', 'modifying_mutation', C.geneticVariation),
  edge('e-gv-protective', 'genetic_variation', 'protective_variant', C.geneticVariation),

  // Drug Response Variant → Pharmacogenomics
  edge('e-drug-pharma', 'drug_response_variant', 'pharmacogenomics', C.geneticVariation),

  // Causal Mutation → L6
  edge('e-cm-somatic', 'causal_mutation', 'somatic_causal_mutation', C.geneticVariation),
  edge('e-cm-germline', 'causal_mutation', 'germline_causal_mutation', C.geneticVariation),

  // Modifying Mutation → L6
  edge('e-mm-somatic', 'modifying_mutation', 'somatic_modifying_mutation', C.geneticVariation),
  edge('e-mm-germline', 'modifying_mutation', 'germline_modifying_mutation', C.geneticVariation),
];

const RELEASE_263_EDGES: Edge[] = [
  // Causal or Contributing → Accumulation and Aggregation + Activity
  edge('e-causal-accum', 'causal_or_contributing', 'accumulation_and_aggregation', C.causalOrContributing),
  edge('e-causal-act', 'causal_or_contributing', 'activity', C.causalOrContributing),

  // Altered Expression → Upregulation + Downregulation
  edge('e-altexpr-up', 'altered_expression', 'upregulation', C.alteredExpression),
  edge('e-altexpr-down', 'altered_expression', 'downregulation', C.alteredExpression),

  // Genetic Variation → QTL
  edge('e-gv-qtl', 'genetic_variation', 'qtl', C.geneticVariation),
];

const NODES: OntologyEntityNode[] = [...BASE_NODES, ...(isRelease263Enabled ? RELEASE_263_NODES : [])];

const EDGES: Edge[] = [...BASE_EDGES, ...(isRelease263Enabled ? RELEASE_263_EDGES : [])];

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
      <span style={{ width: 11, height: 11, borderRadius: 3, background: color, flexShrink: 0 }} />
      <span style={{ fontSize: 11, color: '#444', fontFamily: 'Arial, sans-serif' }}>{label}</span>
    </div>
  );
}

export { BASE_NODES, RELEASE_263_NODES, BASE_EDGES, RELEASE_263_EDGES, NODES, EDGES };

export default function DISGENETOntologyGDAFlow() {
  return (
    <div
      style={{
        width: '100%',
        height: 1060,
        borderRadius: 12,
        overflow: 'hidden',
        border: '1px solid #e0e8f0',
      }}
    >
      <ReactFlow
        nodes={NODES}
        edges={EDGES}
        nodeTypes={NODE_TYPES}
        fitView
        fitViewOptions={{ padding: 0.08 }}
        minZoom={0.1}
        maxZoom={2}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        panOnDrag
        zoomOnScroll
        proOptions={{ hideAttribution: true }}
        style={{ background: '#f8fafc' }}
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#dde4ed" />
        <Controls style={{ background: 'transparent', border: 'none', boxShadow: 'none', gap: 4 }} />
        <Panel position="top-left">
          <div
            style={{
              background: 'rgba(255,255,255,0.94)',
              border: '1px solid #e0e8f0',
              borderRadius: 8,
              padding: '9px 14px',
            }}
          >
            <div
              style={{
                fontSize: 9,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: '#999',
                marginBottom: 7,
              }}
            >
              Legend
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <LegendItem color={C.causalOrContributing} label={'Causal / Contributing'} />
              <LegendItem color={C.biomarker} label="Biomarker" />
              <LegendItem color={C.therapeuticTarget} label="Therapeutic Target" />
              <LegendItem color={C.nonCodingRna} label={'Non-coding RNA Regulation'} />
              <LegendItem color={C.alteredExpression} label="Altered Expression" />
              <LegendItem color={C.postTranslational} label={'Post-translational Mod.'} />
              <LegendItem color={C.genomicAlterations} label="Genomic Alterations" />
              <LegendItem color={C.epigenomicAlterations} label="Epigenomic Alterations" />
              <LegendItem color={C.geneticVariation} label="Genetic Variation" />
            </div>
          </div>
        </Panel>
      </ReactFlow>
      <style>{`
        .react-flow__controls-button {
          border-radius: 50% !important;
          width: 28px !important;
          height: 28px !important;
          box-shadow: 0 1px 4px rgba(0,0,0,0.14) !important;
          border: 1px solid #e0e8f0 !important;
          background: #fff !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        .react-flow__controls-button:hover { background: #f0f7ff !important; }
        .react-flow__controls-button svg { max-width: 12px; max-height: 12px; }
      `}</style>
    </div>
  );
}
